import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { toast } from 'sonner';
import PrinterSlotPanel from '../components/PrinterSlotPanel';
import {
  staffGetConfig,
  staffSetOrdersAccepting,
  staffGetOpenOrders,
  staffCloseOrder,
  staffCancelOrder,
  staffCancelLine,
  staffCashSession,
  staffCashOpen,
  staffCashClose,
  staffCashierPendingPrint,
  staffPrintAck,
  getAdminOrders,
  updateAdminOrder,
} from '../lib/api';
import { todayMexicoYmd } from '../lib/dateMexico';
import { printEscPosLines } from '../lib/thermalPrint/webBluetoothPrinter';
import { buildCashierTicketLines } from '../lib/thermalPrint/escPos';
import {
  Loader2,
  Banknote,
  CreditCard,
  Landmark,
  Printer,
  LayoutGrid,
  Menu,
  Trash2,
  Power,
  PowerOff,
} from 'lucide-react';

const POLL_MS = 10000;

function money(n) {
  return `$${Number(n || 0).toFixed(2)}`;
}

function isWaOrder(o) {
  const ch = String(o?.channel || o?.source || '').toLowerCase();
  return ch === 'whatsapp' || ch === 'wa' || ch === 'telefono';
}

function isWebOrder(o) {
  const ch = String(o?.channel || o?.source || '').toLowerCase();
  return ch === 'web';
}

/** Monto ya cobrado: si el pago en linea (Mercado Pago) esta confirmado, cuenta el total. */
function paidOf(o) {
  const paid = Number(o?.paid_amount || 0);
  if (String(o?.payment_status || '') === 'paid') return Math.max(paid, Number(o?.total || 0));
  return paid;
}

function isTableOrCounter(o) {
  return !isWaOrder(o) && !isWebOrder(o);
}

function orderLabel(o) {
  if (o.table_number != null && o.table_number !== '') return `Mesa ${o.table_number}`;
  if (o.service_type === 'takeaway' || o.delivery_type === 'pickup') {
    return `Para llevar${o.customer_name ? `: ${o.customer_name}` : ''}`;
  }
  if (isWebOrder(o)) return 'Sitio web';
  if (isWaOrder(o)) return String(o?.source || '').toLowerCase() === 'telefono' ? 'Teléfono' : 'WhatsApp';
  return o.customer_name || 'Mostrador';
}

function lineDescription(ln) {
  const parts = [];
  if (ln.size_label) parts.push(ln.size_label);
  const extras = (ln.extra_toppings || []).map((t) => t.name).filter(Boolean);
  if (extras.length) parts.push(`Extra: ${extras.join(', ')}`);
  if (ln.half_specialty_name) parts.push(`Mitad: ${ln.half_specialty_name}`);
  if (ln.kitchen_note) parts.push(ln.kitchen_note);
  if (ln.notes) parts.push(ln.notes);
  return parts.join(' · ');
}

function MethodBtn({ active, onClick, icon, label, disabled }) {
  return (
    <button
      type="button"
      disabled={disabled}
      onClick={onClick}
      className={`flex flex-col items-center justify-center gap-1 rounded-xl border-2 min-h-[4.5rem] px-2 py-2 text-sm font-semibold touch-manipulation disabled:opacity-40 ${
        active
          ? 'border-orange-500 bg-orange-950/40 text-orange-100'
          : 'border-zinc-700 bg-zinc-950 text-zinc-300'
      }`}
    >
      {icon}
      {label}
    </button>
  );
}

export default function AdminCash() {
  const [config, setConfig] = useState(null);
  const [orders, setOrders] = useState([]);
  const [waPending, setWaPending] = useState([]);
  const [session, setSession] = useState(null);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [selectedId, setSelectedId] = useState(null);
  const [pickingOrders, setPickingOrders] = useState(true);
  const [menuOpen, setMenuOpen] = useState(false);
  const [payMethod, setPayMethod] = useState('cash');
  const [tip, setTip] = useState(0);
  const [cashAmount, setCashAmount] = useState('');
  const menuRef = useRef(null);

  const load = useCallback(async () => {
    try {
      const [cfg, open, sess, pendingReview] = await Promise.all([
        staffGetConfig(),
        staffGetOpenOrders(),
        staffCashSession(),
        getAdminOrders('pending_review', todayMexicoYmd()).catch(() => []),
      ]);
      setConfig(cfg);
      setOrders(Array.isArray(open) ? open : []);
      setSession(sess);
      setWaPending(Array.isArray(pendingReview) ? pendingReview : []);
      try {
        const pending = await staffCashierPendingPrint();
        for (const o of pending || []) {
          try {
            await printEscPosLines('cashier', buildCashierTicketLines(o));
            await staffPrintAck(o.id, 'cashier');
          } catch {
            /* impresora no lista */
          }
        }
      } catch {
        /* ignore */
      }
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Error al cargar caja');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
    const t = window.setInterval(load, POLL_MS);
    return () => window.clearInterval(t);
  }, [load]);

  useEffect(() => {
    const onDoc = (e) => {
      if (menuRef.current && !menuRef.current.contains(e.target)) setMenuOpen(false);
    };
    document.addEventListener('mousedown', onDoc);
    return () => document.removeEventListener('mousedown', onDoc);
  }, []);

  useEffect(() => {
    if (
      selectedId &&
      !orders.some((o) => o.id === selectedId) &&
      !waPending.some((o) => o.id === selectedId)
    ) {
      setSelectedId(null);
      setPickingOrders(true);
      setCashAmount('');
      setTip(0);
    }
  }, [orders, waPending, selectedId]);

  const selected = useMemo(() => {
    return (
      orders.find((o) => o.id === selectedId) ||
      waPending.find((o) => o.id === selectedId) ||
      null
    );
  }, [orders, waPending, selectedId]);

  const waOrders = useMemo(() => {
    const fromOpen = orders.filter(isWaOrder);
    const ids = new Set(orders.map((o) => o.id));
    const pending = (waPending || []).filter((o) => !ids.has(o.id) && !isWebOrder(o));
    return [...fromOpen, ...pending];
  }, [orders, waPending]);

  const otherOrders = useMemo(() => orders.filter(isTableOrCounter), [orders]);
  const webOrders = useMemo(() => {
    const ids = new Set(orders.map((o) => o.id));
    const pending = (waPending || []).filter((o) => !ids.has(o.id) && isWebOrder(o));
    return [...orders.filter(isWebOrder), ...pending];
  }, [orders, waPending]);

  const activeLines = useMemo(
    () => (selected?.lines || []).filter((ln) => ln.line_status !== 'cancelled'),
    [selected]
  );

  const paidAmount = paidOf(selected);
  const billTotal = Number(selected?.total || 0);
  const pending = Math.max(0, billTotal - paidAmount);
  const totalRequired = pending + Number(tip || 0);
  const cashReceived = Number(cashAmount || 0);
  const hasEnoughCash = cashReceived + 0.001 >= totalRequired;
  const change = hasEnoughCash ? Math.max(0, cashReceived - totalRequired) : 0;

  const selectOrder = (id) => {
    setSelectedId(id);
    setPickingOrders(false);
    setPayMethod('cash');
    setTip(0);
    const o =
      orders.find((x) => x.id === id) || waPending.find((x) => x.id === id);
    const due = Math.max(0, Number(o?.total || 0) - paidOf(o));
    setCashAmount(due > 0 ? String(due) : '');
  };

  const toggleAccepting = async (accepting) => {
    setBusy(true);
    try {
      const res = await staffSetOrdersAccepting(accepting);
      setConfig((c) => ({ ...c, ...res }));
      toast.success(accepting ? 'Recibiendo pedidos' : 'Pedidos en pausa');
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'No se pudo actualizar');
    } finally {
      setBusy(false);
    }
  };

  const confirmWa = async (orderId) => {
    setBusy(true);
    try {
      await updateAdminOrder(orderId, { status: 'preparing', payment_status: 'paid' });
      toast.success('Pedido confirmado → cocina');
      await load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Error al confirmar');
    } finally {
      setBusy(false);
    }
  };

  const charge = async () => {
    if (!selected?.id) return;
    if (session?.status !== 'open') {
      toast.error('Abre la sesión de caja primero');
      return;
    }
    const alreadyPaid = pending <= 0.01;
    if (!alreadyPaid && payMethod === 'cash' && !hasEnoughCash) {
      toast.error('Efectivo insuficiente');
      return;
    }
    const pmMap = { cash: 'store_cash', card: 'mercadopago', spei: 'transfer' };
    setBusy(true);
    try {
      const notes = tip > 0.01 ? `Propina: ${Number(tip).toFixed(2)}` : '';
      const closed = await staffCloseOrder(selected.id, {
        payment_method: alreadyPaid
          ? selected.payment_method || pmMap[payMethod] || 'store_cash'
          : pmMap[payMethod] || 'store_cash',
        notes,
      });
      try {
        await printEscPosLines('cashier', buildCashierTicketLines(closed));
        await staffPrintAck(selected.id, 'cashier');
      } catch {
        /* optional */
      }
      toast.success(alreadyPaid ? 'Pedido cerrado' : 'Cobro registrado');
      setSelectedId(null);
      setPickingOrders(true);
      setCashAmount('');
      setTip(0);
      await load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'No se pudo cobrar');
    } finally {
      setBusy(false);
    }
  };

  const printBill = async (order) => {
    try {
      await printEscPosLines('cashier', buildCashierTicketLines(order));
      toast.success('Cuenta impresa');
    } catch (err) {
      toast.error(err?.message || 'Sin impresora. Empareja arriba y prueba.');
    }
  };

  const accepting = config?.accepting_orders !== false;

  const renderOrderCard = (o) => {
    const paid = paidOf(o);
    const due = Math.max(0, Number(o.total || 0) - paid);
    const payLabel =
      o.payment_status === 'paid' ? 'Pagado' : paid > 0 ? 'Parcial' : 'Sin pagar';
    return (
      <div
        key={o.id}
        className="relative rounded-xl border border-zinc-700 bg-zinc-900/80"
      >
        <button
          type="button"
          title="Imprimir"
          onClick={(e) => {
            e.stopPropagation();
            printBill(o);
          }}
          className="absolute top-2 right-2 p-1.5 rounded-lg text-zinc-400 hover:text-orange-400 touch-manipulation"
        >
          <Printer size={16} />
        </button>
        <button
          type="button"
          onClick={() => selectOrder(o.id)}
          className="w-full text-left p-3 pr-10 touch-manipulation"
        >
          <div className="flex justify-between gap-2">
            <span className="font-semibold">
              Pedido #{o.order_number} · {orderLabel(o)}
            </span>
            <span className="font-bold tabular-nums shrink-0">{money(o.total)}</span>
          </div>
          <p className="text-xs text-zinc-400 mt-1">
            {o.customer_name && (isWaOrder(o) || isWebOrder(o)) ? `${o.customer_name} · ` : ''}
            {o.status === 'pending_review' ? (
              <span className="text-amber-400 font-medium">Pendiente confirmación · </span>
            ) : null}
            Pagado: {money(paid)} · {payLabel}
            {due > 0.01 ? (
              <span className="text-orange-400"> · Pendiente: {money(due)}</span>
            ) : null}
          </p>
        </button>
      </div>
    );
  };

  if (loading) {
    return (
      <div className="min-h-[60vh] flex items-center justify-center bg-zinc-950 text-zinc-400 rounded-2xl">
        <Loader2 className="animate-spin mr-2" /> Cargando caja…
      </div>
    );
  }

  return (
    <div
      className="rounded-2xl bg-zinc-950 text-zinc-100 -mx-2 sm:mx-0 p-3 sm:p-4 min-h-[calc(100vh-7rem)] flex flex-col gap-3"
      data-testid="admin-caja"
    >
      <div className="shrink-0 flex justify-end items-center gap-2 flex-wrap">
        <PrinterSlotPanel slot="cashier" compact />
        <div className="relative" ref={menuRef}>
          <button
            type="button"
            onClick={() => setMenuOpen((v) => !v)}
            className="inline-flex h-12 w-12 items-center justify-center rounded-xl border border-zinc-700 bg-zinc-900 text-zinc-200 touch-manipulation"
            aria-label="Opciones de caja"
          >
            <Menu size={24} />
          </button>
          {menuOpen && (
            <div className="absolute right-0 z-40 mt-2 w-[min(22rem,calc(100vw-1.25rem))] rounded-2xl border border-zinc-700 bg-zinc-900 p-4 shadow-lg space-y-3">
              {!session || session.status !== 'open' ? (
                <button
                  type="button"
                  disabled={busy}
                  className="w-full min-h-12 rounded-xl bg-orange-600 font-semibold"
                  onClick={async () => {
                    setBusy(true);
                    try {
                      await staffCashOpen();
                      toast.success('Caja abierta');
                      setMenuOpen(false);
                      await load();
                    } finally {
                      setBusy(false);
                    }
                  }}
                >
                  Abrir caja
                </button>
              ) : (
                <>
                  <p className="text-base font-semibold text-emerald-400">Caja abierta</p>
                  <button
                    type="button"
                    disabled={busy}
                    className="w-full min-h-12 rounded-xl border border-zinc-600 font-semibold"
                    onClick={async () => {
                      if (!window.confirm('¿Cerrar turno de caja?')) return;
                      setBusy(true);
                      try {
                        await staffCashClose();
                        toast.success('Caja cerrada');
                        setMenuOpen(false);
                        await load();
                      } finally {
                        setBusy(false);
                      }
                    }}
                  >
                    Cerrar caja
                  </button>
                </>
              )}

              <div className="border-t border-zinc-700 pt-3 space-y-3">
                <div className="flex items-center justify-between gap-3">
                  <div className="min-w-0 flex items-center gap-2.5">
                    <span
                      className={`flex h-9 w-9 shrink-0 items-center justify-center rounded-full ${
                        accepting ? 'bg-emerald-600 text-white' : 'bg-rose-600 text-white'
                      }`}
                    >
                      {accepting ? <Power size={18} /> : <PowerOff size={18} />}
                    </span>
                    <span className="text-base font-medium leading-tight">
                      {accepting ? 'Recibiendo pedidos' : 'Pedidos pausados'}
                    </span>
                  </div>
                  <button
                    type="button"
                    disabled={busy}
                    onClick={() => toggleAccepting(!accepting)}
                    className={`relative inline-flex h-9 w-16 shrink-0 items-center rounded-full transition-colors disabled:opacity-50 touch-manipulation ${
                      accepting ? 'bg-emerald-600' : 'bg-zinc-600'
                    }`}
                  >
                    <span
                      className={`inline-block h-7 w-7 transform rounded-full bg-white shadow transition-transform ${
                        accepting ? 'translate-x-8' : 'translate-x-1'
                      }`}
                    />
                  </button>
                </div>
                {!accepting && config?.orders_paused_message ? (
                  <p className="text-xs text-zinc-400 line-clamp-3">{config.orders_paused_message}</p>
                ) : null}
              </div>
            </div>
          )}
        </div>
      </div>

      {selected && !pickingOrders && (
        <button
          type="button"
          onClick={() => {
            setPickingOrders(true);
            setSelectedId(null);
          }}
          className="shrink-0 w-full rounded-2xl border-2 border-orange-500 bg-orange-950/30 px-4 py-3 min-h-[52px] touch-manipulation flex items-center justify-center gap-3 text-left hover:bg-orange-950/50 transition-colors"
        >
          <LayoutGrid size={22} className="shrink-0 text-orange-400" />
          <span className="flex-1 min-w-0">
            <span className="block text-base sm:text-lg font-bold text-orange-50 truncate">
              #{selected.order_number} · {orderLabel(selected)}
            </span>
            <span className="block text-xs text-orange-200/80">Toca para volver a los pedidos</span>
          </span>
        </button>
      )}

      {pickingOrders ? (
        <div className="flex-1 min-h-0 overflow-hidden flex flex-col rounded-2xl border border-zinc-800 bg-zinc-900/80 p-3 sm:p-4">
          <h2 className="font-semibold mb-3 shrink-0 text-lg">Pedidos abiertos</h2>
          <div className="space-y-4 flex-1 min-h-0 overflow-auto">
            <div>
              <div className="flex items-center gap-2 mb-2">
                <h3 className="text-sm font-bold uppercase tracking-wide text-zinc-500">WhatsApp</h3>
                <span className="rounded-full bg-emerald-900/80 text-emerald-200 text-xs font-semibold px-2 py-0.5">
                  {waOrders.length}
                </span>
              </div>
              {waOrders.length ? (
                <div className="space-y-2">
                  {waOrders.map((o) => renderOrderCard(o))}
                </div>
              ) : (
                <p className="text-xs text-zinc-500">Sin pedidos de WhatsApp</p>
              )}
            </div>

            <div>
              <div className="flex items-center gap-2 mb-2">
                <h3 className="text-sm font-bold uppercase tracking-wide text-zinc-500">Sitio web</h3>
                <span className="rounded-full bg-sky-900/80 text-sky-200 text-xs font-semibold px-2 py-0.5">
                  {webOrders.length}
                </span>
              </div>
              {webOrders.length ? (
                <div className="space-y-2">{webOrders.map((o) => renderOrderCard(o))}</div>
              ) : (
                <p className="text-xs text-zinc-500">Sin pedidos del sitio web</p>
              )}
            </div>

            <div>
              <div className="flex items-center gap-2 mb-2">
                <h3 className="text-sm font-bold uppercase tracking-wide text-zinc-500">
                  Mesa / Mostrador
                </h3>
                <span className="rounded-full bg-zinc-700 text-zinc-200 text-xs font-semibold px-2 py-0.5">
                  {otherOrders.length}
                </span>
              </div>
              {otherOrders.length ? (
                <div className="space-y-2">{otherOrders.map((o) => renderOrderCard(o))}</div>
              ) : (
                <p className="text-xs text-zinc-500">Sin pedidos de mesa/mostrador</p>
              )}
            </div>
          </div>
        </div>
      ) : (
        <div className="flex-1 min-h-0 overflow-auto rounded-2xl border border-zinc-800 bg-zinc-900/80 p-3 sm:p-4">
          <h2 className="font-semibold mb-4 text-lg">Cobrar</h2>
          {!selected ? (
            <p className="text-zinc-500 text-sm">Selecciona un pedido</p>
          ) : (
            <div className="space-y-4">
              {selected.status === 'pending_review' && (
                <button
                  type="button"
                  disabled={busy}
                  onClick={() => confirmWa(selected.id)}
                  className="w-full min-h-12 rounded-xl bg-amber-600 font-semibold touch-manipulation"
                >
                  Confirmar pedido → cocina
                </button>
              )}

              <div className="space-y-3 rounded-xl border border-zinc-700 p-3">
                <p className="text-sm font-semibold">Detalle del pedido</p>
                {(selected.opened_by_name || selected.closed_by_name) && (
                  <p className="text-xs text-zinc-500">
                    Atendió: {selected.opened_by_name || selected.closed_by_name}
                  </p>
                )}
                <ul className="space-y-2 max-h-48 overflow-auto">
                  {activeLines.map((ln) => {
                    const desc = lineDescription(ln);
                    return (
                      <li key={ln.line_id} className="flex items-center gap-2 text-sm">
                        <span className="flex-1 min-w-0">
                          {ln.qty}x {ln.name}
                          {desc ? (
                            <span className="text-zinc-400"> ({desc})</span>
                          ) : null}
                        </span>
                        <span className="shrink-0 font-medium tabular-nums">
                          {money(ln.line_total)}
                        </span>
                        <button
                          type="button"
                          title="Quitar artículo"
                          disabled={busy}
                          onClick={async () => {
                            const msg =
                              ln.qty > 1
                                ? `¿Cancelar ${ln.qty}x ${ln.name}?`
                                : `¿Cancelar ${ln.name}?`;
                            if (!window.confirm(msg)) return;
                            setBusy(true);
                            try {
                              const updated = await staffCancelLine(selected.id, ln.line_id);
                              if (updated?.status === 'cancelled') {
                                toast.success('Pedido cancelado (sin artículos)');
                                setSelectedId(null);
                                setPickingOrders(true);
                              } else {
                                setSelectedId(updated.id);
                                setOrders((prev) =>
                                  prev.map((x) => (x.id === updated.id ? updated : x))
                                );
                              }
                              await load();
                            } catch (err) {
                              toast.error(err?.response?.data?.detail || 'No se pudo quitar');
                            } finally {
                              setBusy(false);
                            }
                          }}
                          className="shrink-0 p-2 rounded-lg text-rose-400 hover:bg-rose-950/40 touch-manipulation"
                        >
                          <Trash2 size={16} />
                        </button>
                      </li>
                    );
                  })}
                </ul>
                {!activeLines.length && (
                  <p className="text-xs text-zinc-500">Sin artículos activos</p>
                )}
                <button
                  type="button"
                  disabled={busy || paidAmount > 0.01 || selected.payment_status === 'paid'}
                  onClick={async () => {
                    if (!window.confirm('¿Cancelar el pedido completo y liberar la mesa?')) return;
                    setBusy(true);
                    try {
                      await staffCancelOrder(selected.id);
                      toast.success('Pedido cancelado');
                      setSelectedId(null);
                      setPickingOrders(true);
                      await load();
                    } catch (err) {
                      toast.error(err?.response?.data?.detail || 'No se pudo cancelar');
                    } finally {
                      setBusy(false);
                    }
                  }}
                  className="w-full min-h-11 rounded-xl border border-rose-800 text-rose-300 font-semibold inline-flex items-center justify-center gap-2 disabled:opacity-40 touch-manipulation"
                >
                  <Trash2 size={16} />
                  Cancelar pedido completo
                </button>
              </div>

              <div className="flex justify-between text-lg font-bold">
                <span>Pendiente</span>
                <span className="tabular-nums">{money(pending)}</span>
              </div>

              {tip > 0.01 && (
                <div className="flex justify-between text-base font-semibold text-zinc-300">
                  <span>Total a cobrar</span>
                  <span className="tabular-nums">{money(totalRequired)}</span>
                </div>
              )}

              {pending > 0.01 && (
                <>
                  <div>
                    <label className="text-sm block mb-1">Propina</label>
                    <input
                      type="number"
                      min={0}
                      step="0.01"
                      value={tip || ''}
                      onChange={(e) => setTip(+e.target.value || 0)}
                      className="w-full rounded-xl bg-zinc-950 border border-zinc-600 px-3 py-2.5 outline-none focus:border-orange-500"
                    />
                  </div>

                  <div>
                    <label className="text-sm block mb-2">Método de pago</label>
                    <div className="grid grid-cols-3 gap-2">
                      <MethodBtn
                        active={payMethod === 'cash'}
                        onClick={() => setPayMethod('cash')}
                        icon={<Banknote size={18} />}
                        label="Efectivo"
                      />
                      <MethodBtn
                        active={payMethod === 'card'}
                        onClick={() => setPayMethod('card')}
                        icon={<CreditCard size={18} />}
                        label="Tarjeta"
                      />
                      <MethodBtn
                        active={payMethod === 'spei'}
                        onClick={() => setPayMethod('spei')}
                        icon={<Landmark size={18} />}
                        label="SPEI"
                      />
                    </div>
                  </div>

                  {payMethod === 'cash' && (
                    <>
                      <div>
                        <label className="text-sm block mb-1">Efectivo recibido</label>
                        <input
                          type="number"
                          min={0}
                          step="0.01"
                          value={cashAmount}
                          onChange={(e) => setCashAmount(e.target.value)}
                          placeholder={`Mínimo ${money(totalRequired)}`}
                          className="w-full rounded-xl bg-zinc-950 border border-zinc-600 px-3 py-2.5 outline-none focus:border-orange-500"
                        />
                      </div>
                      {cashAmount && !hasEnoughCash && (
                        <p className="text-sm text-rose-400 font-medium">
                          Efectivo insuficiente. Faltan {money(totalRequired - cashReceived)}
                        </p>
                      )}
                      {hasEnoughCash && cashAmount !== '' && (
                        <p className="text-emerald-400 font-medium">Cambio: {money(change)}</p>
                      )}
                      <div className="flex flex-col sm:flex-row gap-2">
                        <button
                          type="button"
                          disabled={busy || !cashAmount || !hasEnoughCash}
                          onClick={charge}
                          className="flex-1 min-h-[52px] rounded-2xl bg-orange-600 hover:bg-orange-500 disabled:opacity-40 py-3 text-base font-bold touch-manipulation"
                        >
                          Cobrar efectivo
                        </button>
                        <button
                          type="button"
                          disabled={!activeLines.length}
                          onClick={() => printBill(selected)}
                          className="flex-1 min-h-[52px] rounded-2xl border border-zinc-500 py-3 text-base font-semibold inline-flex items-center justify-center gap-2 touch-manipulation disabled:opacity-40"
                        >
                          <Printer size={20} />
                          Imprimir cuenta
                        </button>
                      </div>
                    </>
                  )}

                  {payMethod === 'card' && (
                    <button
                      type="button"
                      disabled={busy}
                      onClick={charge}
                      className="w-full min-h-[52px] rounded-2xl bg-orange-600 hover:bg-orange-500 disabled:opacity-40 py-3 text-base font-bold touch-manipulation"
                    >
                      Cobrar tarjeta
                    </button>
                  )}

                  {payMethod === 'spei' && (
                    <button
                      type="button"
                      disabled={busy}
                      onClick={charge}
                      className="w-full min-h-[52px] rounded-2xl border border-zinc-500 py-3 text-base font-bold touch-manipulation disabled:opacity-40"
                    >
                      Cobrar por SPEI
                    </button>
                  )}
                </>
              )}

              {pending <= 0.01 && (
                <button
                  type="button"
                  disabled={busy}
                  onClick={charge}
                  className="w-full min-h-[52px] rounded-2xl border border-zinc-500 py-3 font-semibold"
                >
                  Cerrar pedido / Liberar mesa
                </button>
              )}
            </div>
          )}
        </div>
      )}
    </div>
  );
}
